<template>
  <div ref="rootRef" class="newsletter">
    <!-- <button class="newsletter__trigger" @click="open">
      <span class="newsletter__trigger__label p-medium">
        Subscribe to our newsletter
      </span>
      <SvgSprite
        symbol="icons-paper"
        size="20"
        class="newsletter__trigger__icon"
      />
    </button> -->
    <GBtn
      :background="background"
      :foreground="foreground"
      icon="icons-paper"
      size="tiny"
      @click="open"
    >
      {{ t('newsletter-subscribe') }}
    </GBtn>

    <Teleport to="body">
      <transition name="fade">
        <div v-if="chrome.isNewsletterVisible" class="newsletter__modal">
          <div class="newsletter__modal-outer">
            <div class="newsletter__modal-inner">
              <div class="newsletter__form__header">
                <span class="section-label">
                  {{ t('newsletter-subscribe') }}
                </span>
                <button class="newsletter__form__header__close">
                  <SvgSprite symbol="ui-cross" size="17" @click="close" />
                </button>
              </div>
              <form
                v-if="!isSubmitted"
                ref="$form"
                v-outside="close"
                novalidate
                class="newsletter__form"
                @submit.prevent="onSubmit"
              >
                <span class="h1 newsletter__form__title">
                  {{ t('newsletter-title') }}
                </span>
                <p class="newsletter__form__subtitle">
                  {{ t('newsletter-subtitle') }}
                </p>
                <VInput
                  v-model="form.firstName"
                  :label="t('form-firstname')"
                  name="firstname"
                  class="form-field"
                />
                <VInput
                  v-model="form.lastName"
                  :label="t('form-lastname')"
                  name="lastname"
                  class="form-field"
                />
                <VInput
                  v-model="form.email"
                  :label="t('form-email')"
                  name="email"
                  type="email"
                  class="form-field"
                />

                <VInput
                  v-model="form.company"
                  :label="t('form-company')"
                  name="company"
                  type="string"
                  class="form-field"
                  autocomplete="company"
                  :required="false"
                />

                <VSelect
                  v-if="chrome.mailchimpCountries[language]"
                  v-model="form.country"
                  :label="t('form-country')"
                  name="country"
                  class="form-field"
                  autocomplete="country-name"
                  :options="chrome.mailchimpCountries[language]!"
                />

                <VSelect
                  v-model="form.lang"
                  :key="language"
                  :label="t('form-lang')"
                  name="lang"
                  class="form-field"
                  autocomplete="language"
                  :required="true"
                  :options="{
                    EN: 'en',
                    FR: 'fr',
                  }"
                />

                <div class="form-field fields-block" data-input>
                  <span class="form-title">{{ t('form-interests') }}</span>
                  <div
                    v-for="item in [
                      {
                        label: t('form-piqure-rappel'),
                        description: t('form-piqure-rappel-desc'),
                        value: 'piqure-rappel',
                      },
                      {
                        label: t('form-agency-life'),
                        description: t('form-agency-life-desc'),
                        value: 'agency-life',
                      },
                      {
                        label: t('form-services'),
                        description: t('form-services-desc'),
                        value: 'services',
                      },
                    ]"
                  >
                    <VCheckbox
                      :id="item.value"
                      :key="item.value"
                      v-model="form.interests"
                      :required="interestsRequired"
                      name="interests"
                      :value="item.value"
                      :label="`${item.label}<div class='newsletter__form__checkbox-desc'>${item.description}</div>`"
                      class="form-field--small checkbox newsletter__form__checkbox"
                    />
                  </div>
                  <p class="input__error">{{ t('form-invalid') }}</p>
                </div>

                <Feedback
                  v-if="hasError"
                  :content="feedback"
                  class="form-field"
                />
                <div class="turnstile-widget-newsletter"></div>

                <div class="row end-xxs">
                  <GBtn type="submit" :disabled="isLoading" class="btn--small">
                    {{ t('newsletter-submit') }}
                  </GBtn>
                </div>
              </form>
              <div v-else class="newsletter__done">
                <span class="h1 newsletter__form__title">{{
                  t('newsletter-done-title')
                }}</span>
                <p class="p-big">
                  {{ t('newsletter-done-text') }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </transition>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { useAxios } from '@monofront/vue-axios-plugin'
import { computed, reactive, ref } from 'vue'

import VInput from '@/components/form/Input.vue'
import VSelect from '@/components/form/Select.vue'
import VCheckbox from '@/components/form/Checkbox.vue'
import Feedback from '@/components/ui/Feedback.vue'
import { getApiUrl } from '@/config/app'
import {
  getTurnstileToken,
  removeTurnstile,
  validateForm,
} from '@/modules/form'
import { logger } from '@/utils/logger'
import { push } from '@/utils/tracking'
import { useChromeStore } from '@/stores/chrome'

defineProps({
  background: {
    type: String,
    default: null,
  },
  foreground: {
    type: String,
    default: null,
  },
})

const chrome = useChromeStore()
const axios = useAxios()

const rootRef = ref<HTMLElement>()
const hasError = ref(false)
const isLoading = ref(false)
const isSubmitted = ref(false)

const feedback = reactive({
  type: 'warning',
  text: '<strong>An error has occured</strong>Please check the mandatory fields and try again.',
})

const { t, language } = useChromeStore()

const getDefaultFormValue = () => ({
  firstName: '',
  lastName: '',
  email: '',
  country: '',
  company: '',
  lang: language,
  interests: ['piqure-rappel', 'agency-life'],
})

const close = () => {
  chrome.isNewsletterVisible = false
  isSubmitted.value = false
  form.value = getDefaultFormValue()
  document.documentElement.classList.remove('no-scroll')
}

const open = () => {
  chrome.openNewsletterPopup()
}

const form = ref(getDefaultFormValue())

// Validation custom pour les groupes d'intérêt, il faut qu'au moins une checkbox soit cochée.
const interestsRequired = computed(() => form.value.interests.length < 1)

const $form = ref({} as HTMLFormElement)

const onSubmit = async () => {
  const isValid = validateForm($form.value)

  if (!isValid) {
    return
  }

  isLoading.value = true
  hasError.value = false

  const formData = new FormData($form.value)
  let turnstileWidgetID: string

  // 👷‍♂️ Restructure données un tout petit peu: la langue est attendue en temps que "groupe", au même titre que les centre d'intérêts.
  // Tous ceux là sont passés dans un seul tableau "interests".
  formData.delete('lang')
  formData.delete('interests')
  formData.append(
    'interests',
    [...form.value.interests, form.value.lang].toString()
  )
  formData.append('tags', 'website,newsletter,popup-newsletter')

  try {
    const { id, token } = await getTurnstileToken(
      '.turnstile-widget-newsletter'
    )

    if (token) {
      turnstileWidgetID = id
      formData.append('cf-turnstile-response', token)
    }
  } catch (error) {
    console.error(error)
  }

  axios
    .post(`${getApiUrl()}/newsletter`, formData)
    .catch(res => {
      isLoading.value = false
      hasError.value = true
      logger.error(res)

      return res
    })
    .then(res => {
      if (res.data.status === 200) {
        isLoading.value = false
        isSubmitted.value = true

        const ctx = rootRef.value?.closest('.mega-menu')
        const ctaLocation = ctx ? 'menu' : 'footer'
        const dataLayer = {
          event: 'newsletter_subscription',
          ctaLocation,
          formStatus: 'success',
        }

        push(dataLayer)
      } else {
        isLoading.value = false
        hasError.value = true
        logger.error(res)
      }
    })
    .finally(() => {
      removeTurnstile(turnstileWidgetID)
    })
}
</script>

<style lang="scss" scoped>
.newsletter__modal {
  @include get-all-space;

  position: fixed;
  z-index: $header-z-index + 1;
  display: flex;
  justify-content: center;
  align-items: center;
}

.newsletter__modal-outer {
  padding: 1rem;
  background-color: var(--c-background);
  border: 0.1rem solid var(--c-foreground);
  border-radius: 5px;
  box-shadow: 0 0 30px 0 rgba($c-black, 0.2);
}

.newsletter__modal-inner {
  @include scrollbars($foreground-color: var(--c-foreground));

  overflow: auto;
  width: 90vw;
  max-width: 80rem;
  max-height: 80vh;
  padding: 2rem;

  @include mq(m) {
    padding: 6rem;
  }
}

.newsletter__form__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4rem;
  padding-bottom: 3rem;
  border-bottom: 1px solid var(--c-foreground);
  fill: var(--c-foreground);
}

.newsletter__form__header__close {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 4rem;
  height: 4rem;
  padding: 0;
  background-color: transparent;
  border: 0;
  fill: var(--c-foreground);

  &:hover {
    cursor: pointer;
  }
}

.newsletter__form__title {
  // prettier-ignore
  @include fluid(font-size,(xxs: 30px, xxl: 50px));

  // prettier-ignore
  @include fluid(line-height,(xxs: 35px, xxl: 55px));

  display: block;
  margin-bottom: 4rem;
}

.newsletter__form__subtitle {
  // prettier-ignore
  @include fluid(margin-bottom,(xxs: 20px, xxl: 40px));
}

.fields-block {
  position: relative;

  &.has-error {
    .form-title {
      border-width: 0 0 3px;
    }
  }
}

.checkbox {
  align-items: center;
}

:deep(.newsletter__form__checkbox-desc) {
  font-size: 1.2rem;
}

// Décalle un tout petit peu les checkboxes avec descriptions des intérêts newsletter
// Parce qu'avec label + description l'alignement de la checkbox n'est pas très heureux
.newsletter__form__checkbox {
  :deep(.checkbox__label) {
    &::before {
      top: 0.3rem;
    }

    &::after {
      top: 1.1rem;
    }
  }
}
</style>
